import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable } from '@angular/core';

import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { IS_MOBILE } from '../auth/oauth-redirect.tokens';

// Smaller viewport differences can come from browser chrome instead of the keyboard.
const KEYBOARD_THRESHOLD_PX = 60;
export const WEB_KEYBOARD_HEIGHT_VAR = '--web-keyboard-height';

@Injectable({ providedIn: 'root' })
export class WebKeyboardInsetService {
  readonly #document = inject(DOCUMENT);
  readonly #window = inject(WINDOW);
  readonly #isMobileBuild = inject(IS_MOBILE);
  readonly #destroyRef = inject(DestroyRef);

  #frameId: number | null = null;
  #publishedPx = -1;
  #removeListeners: (() => void) | null = null;

  constructor() {
    this.#destroyRef.onDestroy(() => this.stop());
  }

  start(): void {
    const win = this.#window;
    const viewport = win?.visualViewport;
    // Native keyboard compensation already owns the residual inset in Capacitor.
    if (!win || !viewport || this.#isMobileBuild || this.#removeListeners) { return; }

    const onViewportChange = (): void => {
      if (this.#frameId !== null) { return; }
      this.#frameId = win.requestAnimationFrame(() => {
        this.#frameId = null;
        this.#apply(win, viewport);
      });
    };
    viewport.addEventListener('resize', onViewportChange);
    viewport.addEventListener('scroll', onViewportChange);
    this.#removeListeners = (): void => {
      viewport.removeEventListener('resize', onViewportChange);
      viewport.removeEventListener('scroll', onViewportChange);
    };
    this.#apply(win, viewport);
  }

  stop(): void {
    if (!this.#removeListeners) { return; }
    this.#removeListeners();
    this.#removeListeners = null;
    if (this.#frameId !== null) {
      this.#window?.cancelAnimationFrame(this.#frameId);
      this.#frameId = null;
    }
    this.#publishedPx = -1;
    this.#document.documentElement.style.removeProperty(WEB_KEYBOARD_HEIGHT_VAR);
  }

  #apply(win: Window, viewport: VisualViewport): void {
    // A visual viewport scroll already revealed this part of the focused field.
    const overlap = Math.round(win.innerHeight - viewport.height - viewport.offsetTop);
    const inset = overlap > KEYBOARD_THRESHOLD_PX ? overlap : 0;
    if (inset === this.#publishedPx) { return; }
    this.#publishedPx = inset;
    this.#document.documentElement.style.setProperty(WEB_KEYBOARD_HEIGHT_VAR, `${inset}px`);
  }
}
